import { getNextPageParam } from '@lib/utils/reactQuery/infinityQueryHelpers';
import { getClientNotesById } from '@services/external/user.api';
import { useAppSelector } from '@hooks/app/redux';
import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';
import React from 'react';
import { GetUserNotesParams } from 'types/notes';

const PAGE_SIZE = 10;

type QParams = Omit<GetUserNotesParams, 'page' | 'pageSize'> & { providerId: string };

async function handler(page: number = 1, params: QParams) {
  const resData = await getClientNotesById(params.providerId, {
    page,
    pageSize: PAGE_SIZE,
    ...params,
  });

  return resData.data.data;
}

type TQueryData = Awaited<ReturnType<typeof handler>>;

function useClientNotes(
  clientId: string,
  params: Omit<QParams, 'providerId' | 'clientId'> = { direction: 'DESC', sortBy: 'createdAt' },
  options?: Omit<UseInfiniteQueryOptions<TQueryData, any, TQueryData, TQueryData, any>, 'queryKey'>,
) {
  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;

  const { data, ...rest } = useInfiniteQuery(
    ['user:medicalNotes', params, providerId, clientId],
    ({ pageParam }) => handler(pageParam, { ...params, providerId, clientId }),
    {
      ...options,
      getNextPageParam: getNextPageParam,
    },
  );

  const flattenData = React.useMemo(() => {
    return data?.pages.flatMap((p) => p.items) ?? [];
  }, [data]);

  return { data: flattenData, ...rest };
}

export default useClientNotes;
